import { useMemo, useState, type FormEvent } from 'react'

import { isApiError } from '@/api/client'
import { useConfirmMatter } from '@/api/intake'
import {
  stepLabel,
  useConflictCheck,
  useOpenOpposition,
  useOppositionCascade,
  useOppositionReadiness,
  type CascadeStep,
  type ConflictCheckResult,
  type ConflictHit,
  type OfficeReadiness,
} from '@/api/opposition'
import { cleanGrounds } from '@/api/pipeline'
import {
  JURISDICTIONS,
  JURISDICTION_OFFICES,
  jurisdictionLabel,
  type Jurisdiction,
} from '@/api/types'
import {
  Badge,
  Button,
  Card,
  CardHeader,
  EmptyState,
  Field,
  Input,
  ScreenHeader,
  Select,
  Textarea,
  useToast,
} from '@/components/ui'
import { useRouter } from '@/routes'
import { rememberRunDefaults } from '@/runDefaults'

/**
 * Opposition — the applicant's first response.
 *
 * ## What this screen is for, and what it deliberately is not
 *
 * `docs/OPPOSITION_PLAN.md` scopes one document: the counterstatement an
 * applicant files when its mark is opposed. Nothing after it — no evidence
 * rounds, no hearings, no extensions, and no drafting for the opponent's side.
 *
 * The screen has three parts and they are in the order the work happens, which
 * is *not* the order a form would put them:
 *
 * 1. **Who is on the other side** — and the conflict check, run before any
 *    other field matters. We cannot act against an existing client, and there
 *    is no point filling in an application number for a matter that will be
 *    refused. This is the one gate in the platform where the refusal is
 *    professional rather than technical.
 * 2. **The clock** — the deadline series computed from the notice date. An
 *    opposition does not have *a* deadline: the counterstatement is due, and
 *    the opponent's evidence period runs from that due date rather than from
 *    anything printed on the notice. Both are shown, and only the fatal one is
 *    shown as a hard stop.
 * 3. **Open it** — which does the conflict check again, server-side, and is
 *    the run that decides.
 *
 * ## Three things this screen refuses to do
 *
 * - **It never decides that a conflict cleared.** `clears` is a field on the
 *   server's answer and is rendered, not recomputed. A screen that worked out
 *   for itself when a check passed would be a second gate, and the second gate
 *   is always the lenient one.
 * - **It never shows a deadline it has no rule for.** An office with no
 *   registered counterstatement rule (EUIPO — see the plan) renders the
 *   server's `unchecked_reason` where the dates would be. An empty table would
 *   read as "no deadlines", which is the opposite of what it means.
 * - **It never offers drafting when drafting is off.** The two flags come from
 *   `GET /api/opposition/readiness`. The alternative is a button whose endpoint
 *   answers 501, which teaches an analyst that the screen lies.
 *
 * ## Why "not enabled" is a first-class state here
 *
 * Both flags are off by default and the screen's most common rendering is the
 * one that says so. That is not an unfinished state to be tidied away later:
 * the reason drafting is off is that the measurement which would say whether a
 * counterstatement draft is any good cannot run against this corpus, and a
 * proceeding where losing ends the application is the wrong place to ship an
 * unmeasured drafter. The screen says that in the server's own words rather
 * than paraphrasing it into "coming soon".
 */
export function OppositionScreen() {
  const readiness = useOppositionReadiness()
  const conflictCheck = useConflictCheck()
  const openMatter = useOpenOpposition()
  const confirmMatter = useConfirmMatter()
  const toast = useToast()
  const router = useRouter()

  const [office, setOffice] = useState<Jurisdiction>('IN')
  const [opponentName, setOpponentName] = useState('')
  const [opponentCounsel, setOpponentCounsel] = useState('')
  const [applicantName, setApplicantName] = useState('')
  const [applicationNumber, setApplicationNumber] = useState('')
  const [markText, setMarkText] = useState('')
  const [noticeDate, setNoticeDate] = useState('')
  const [printedDeadline, setPrintedDeadline] = useState('')
  const [groundsText, setGroundsText] = useState('')
  // A matter that was opened but not yet confirmed. Opening is not repeatable (the
  // event log refuses DELETE, so a second press would burn a second matter id),
  // so a retry after a failed confirmation confirms this one instead.
  const [pendingMatterId, setPendingMatterId] = useState<string | null>(null)
  const [waived, setWaived] = useState<string[]>([])

  const cascade = useOppositionCascade(office, noticeDate)

  /**
   * The check result, but only while it still describes what is on screen.
   *
   * Editing the opponent's name after a check invalidates the answer, and a
   * stale "clear" beside a changed name is the single most dangerous thing
   * this screen could render — it is a clearance for somebody else. Comparing
   * the proposed name rather than clearing state on every keystroke keeps the
   * result visible while the analyst reads it and drops it the moment the
   * subject changes.
   */
  const conflict: ConflictCheckResult | undefined = useMemo(() => {
    const result = conflictCheck.data
    if (!result) return undefined
    return result.proposed === opponentName.trim() ? result : undefined
  }, [conflictCheck.data, opponentName])

  const officeReadiness: OfficeReadiness | undefined =
    readiness.data?.offices.find((entry) => entry.office === office)

  function runConflictCheck() {
    const name = opponentName.trim()
    if (!name) return
    conflictCheck.mutate(
      { opponent_name: name, conflict_waiver: waived },
      {
        onError: (error) => {
          toast.push('The conflict check could not run', {
            tone: 'error',
            description: isApiError(error) ? error.message : 'Unknown error.',
          })
        },
      },
    )
  }

  /**
   * Acknowledge one matched party.
   *
   * One hit at a time, by name, and never a "waive all" — the server refuses a
   * partial waiver anyway (the hit nobody acknowledged is the one they did not
   * look at), and a single button that cleared everything would be the switch
   * that turns the check off.
   */
  function acknowledge(hit: ConflictHit) {
    const next = [...waived, hit.matched_name]
    setWaived(next)
    conflictCheck.mutate({
      opponent_name: opponentName.trim(),
      conflict_waiver: next,
    })
  }

  /**
   * Open the matter, then confirm it, then hand the person to the Case screen.
   *
   * Confirming is part of opening because the facts on this form are typed by
   * the person pressing the button: there is no machine reading to accept or
   * correct, which is what the confirm step (I1) exists to put a human in
   * front of. Without it the matter sat in INTAKE, and the first run asked for
   * from the Case screen was refused with a 409 no screen explained.
   *
   * The notice date and the pleaded grounds are remembered for the first run
   * (`runDefaults.ts`): the run that answers the notice is started from the
   * Case screen, which has no earlier run to carry them over from.
   */
  async function submit(event: FormEvent) {
    event.preventDefault()
    let matterId = pendingMatterId
    try {
      if (!matterId) {
        const matter = await openMatter.mutateAsync({
          jurisdiction: office,
          proceeding_type: 'OPPOSITION',
          opponent_name: opponentName.trim(),
          opponent_counsel: opponentCounsel.trim() || null,
          applicant_name: applicantName.trim() || null,
          application_number: applicationNumber.trim() || null,
          mark_text: markText.trim() || null,
          deadline_date: printedDeadline || null,
          conflict_waiver: waived,
        })
        matterId = matter.id
        setPendingMatterId(matter.id)
      }
    } catch (error) {
      toast.push('Not opened', {
        tone: 'error',
        description: isApiError(error) ? error.message : 'Unknown error.',
      })
      return
    }

    try {
      await confirmMatter.mutateAsync({
        matterId,
        body: {
          application_number: applicationNumber.trim() || null,
          mark_text: markText.trim() || null,
          applicant_name: applicantName.trim() || null,
          deadline_date: printedDeadline || null,
        },
      })
    } catch (error) {
      toast.push(`${matterId} was opened but not confirmed`, {
        tone: 'error',
        description: `${
          isApiError(error) ? error.message : 'Unknown error.'
        } Press "Open the opposition" again to retry the confirmation; the matter will not be opened twice.`,
      })
      return
    }

    rememberRunDefaults(matterId, { reportDate: noticeDate, grounds: cleanGrounds(groundsText.split(/[\n,]/)) })
    toast.push(`${matterId} opened and confirmed`, {
      tone: 'success',
      description:
        'Conflict-checked and docketed as an opposition. Paste the notice on the Case screen to draft the counterstatement.',
    })
    router.navigate(`/case/${encodeURIComponent(matterId)}`)
  }

  if (readiness.isLoading) {
    return (
      <div className='text-body text-muted'>
        Checking what this deployment can do…
      </div>
    )
  }

  if (readiness.isError || !readiness.data) {
    return (
      <EmptyState
        title='Could not check what this deployment can do'
        description={
          isApiError(readiness.error)
            ? readiness.error.message
            : 'The server did not answer. Reload the page to try again.'
        }
      />
    )
  }

  const { opposition_enabled, drafting_enabled, drafting_disabled_reason } =
    readiness.data

  return (
    <div className='flex flex-col gap-4'>
      <ScreenHeader
        kicker='Inter partes'
        title='Opposition — the first response'
        description='The counterstatement an applicant files when its mark is opposed. Evidence rounds, hearings and extensions are out of scope.'
        actions={
          <>
            <Badge tone={opposition_enabled ? 'ok' : 'outline'}>
              {opposition_enabled ? 'intake on' : 'intake off'}
            </Badge>
            <Badge tone={drafting_enabled ? 'ok' : 'outline'}>
              {drafting_enabled ? 'drafting on' : 'drafting off'}
            </Badge>
          </>
        }
      />

      {!drafting_enabled && drafting_disabled_reason ? (
        <Card>
          <CardHeader
            kicker='Why drafting is off'
            title='This is a decision, not an unfinished feature'
          />
          <p className='mt-2 max-w-[70ch] text-body text-muted'>
            {drafting_disabled_reason}
          </p>
        </Card>
      ) : null}

      <form className='flex flex-col gap-4' onSubmit={submit}>
        {/* ---- 1. the second party -------------------------------------- */}
        <Card>
          <CardHeader
            kicker='Step 1'
            title='Who is on the other side'
            description='Checked before anything else: we cannot act against an existing client, and the log is append-only, so a matter opened in error cannot be un-opened.'
          />
          <div className='mt-3 grid gap-3 md:grid-cols-2'>
            <Field
              label='Opponent'
              required
              hint='As named on the notice of opposition.'
            >
              {({ id, describedBy, invalid }) => (
                <Input
                  id={id}
                  aria-describedby={describedBy}
                  invalid={invalid}
                  value={opponentName}
                  onChange={(event) => setOpponentName(event.target.value)}
                  placeholder="Type the opponent's name from the notice"
                />
              )}
            </Field>
            <Field
              label='Their representative'
              hint="Recorded for the file only. Nothing is ever sent here — mail goes to the instructing agent's address."
            >
              {({ id, describedBy }) => (
                <Input
                  id={id}
                  aria-describedby={describedBy}
                  value={opponentCounsel}
                  onChange={(event) => setOpponentCounsel(event.target.value)}
                  placeholder="Type their agent from the notice, if it names one"
                />
              )}
            </Field>
          </div>

          <div className='mt-3 flex items-center gap-2'>
            <Button
              type='button'
              variant='secondary'
              onClick={runConflictCheck}
              disabled={!opponentName.trim() || conflictCheck.isPending}
            >
              {conflictCheck.isPending ? 'Checking…' : 'Run the conflict check'}
            </Button>
            <span className='text-micro text-muted'>
              Writes nothing. The same check runs again when the matter is
              opened, and that run is the one that decides.
            </span>
          </div>

          {conflict ? (
            <ConflictResult result={conflict} onAcknowledge={acknowledge} />
          ) : null}
        </Card>

        {/* ---- 2. the clock --------------------------------------------- */}
        <Card>
          <CardHeader
            kicker='Step 2'
            title='The clock'
            description="An opposition has a series, not a date: the opponent's evidence period runs from the counterstatement's due date, not from the notice."
          />
          <div className='mt-3 grid gap-3 md:grid-cols-3'>
            <Field label='Office' required>
              {({ id }) => (
                <Select
                  id={id}
                  value={office}
                  onChange={(event) =>
                    setOffice(event.target.value as Jurisdiction)
                  }
                >
                  {JURISDICTIONS.map((value) => (
                    <option key={value} value={value}>
                      {jurisdictionLabel(value)} — {JURISDICTION_OFFICES[value]}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
            <Field
              label='Date on the notice'
              hint='What every computed date below counts from.'
            >
              {({ id, describedBy }) => (
                <Input
                  id={id}
                  type='date'
                  aria-describedby={describedBy}
                  value={noticeDate}
                  onChange={(event) => setNoticeDate(event.target.value)}
                />
              )}
            </Field>
            <Field
              label='Deadline as printed'
              hint="The date written on the papers. It is compared with the computed date when the matter runs."
            >
              {({ id, describedBy }) => (
                <Input
                  id={id}
                  type='date'
                  aria-describedby={describedBy}
                  value={printedDeadline}
                  onChange={(event) => setPrintedDeadline(event.target.value)}
                />
              )}
            </Field>
          </div>

          <OfficeRuleNote readiness={officeReadiness} />

          {noticeDate ? (
            <CascadeTable
              steps={cascade.data?.steps ?? []}
              uncheckedReason={cascade.data?.unchecked_reason ?? null}
              loading={cascade.isLoading}
            />
          ) : (
            <p className='mt-3 text-micro text-muted'>
              Give the date on the notice to see the series.
            </p>
          )}
        </Card>

        {/* ---- 3. open it ------------------------------------------------ */}
        <Card>
          <CardHeader
            kicker='Step 3'
            title='Open the matter'
            description={
              <span title='Recorded in the matter.created event'>
                Runs the conflict check again on the server and records its result in the
                matter&rsquo;s creation record, which the server writes and no caller can forge.
              </span>
            }
          />
          <div className='mt-3 grid gap-3 md:grid-cols-3'>
            <Field label='Applicant' hint='Our client.'>
              {({ id, describedBy }) => (
                <Input
                  id={id}
                  aria-describedby={describedBy}
                  value={applicantName}
                  onChange={(event) => setApplicantName(event.target.value)}
                  placeholder="Type the applicant from the papers"
                />
              )}
            </Field>
            <Field
              label='Application number'
              hint='Optional. The number on the application being opposed, not the opposition number.'
            >
              {({ id, describedBy }) => (
                <Input
                  id={id}
                  mono
                  aria-describedby={describedBy}
                  value={applicationNumber}
                  onChange={(event) => setApplicationNumber(event.target.value)}
                />
              )}
            </Field>
            <Field label='Mark' hint='The mark as applied for.'>
              {({ id, describedBy }) => (
                <Input
                  id={id}
                  aria-describedby={describedBy}
                  value={markText}
                  onChange={(event) => setMarkText(event.target.value)}
                  placeholder="Type the mark from the papers"
                />
              )}
            </Field>
          </div>
          <div className='mt-3'>
            <Field
              label='Grounds pleaded'
              hint='Optional. The grounds the notice raises, one per line or comma-separated, for example 11(1). They are the grounds the counterstatement answers, and they carry into the first run.'
            >
              {({ id, describedBy }) => (
                <Textarea
                  id={id}
                  aria-describedby={describedBy}
                  rows={2}
                  value={groundsText}
                  onChange={(event) => setGroundsText(event.target.value)}
                />
              )}
            </Field>
          </div>

          <div className='mt-3 flex flex-wrap items-center gap-2'>
            <Button
              type='submit'
              disabled={
                !opposition_enabled ||
                !opponentName.trim() ||
                openMatter.isPending ||
                confirmMatter.isPending
              }
            >
              {openMatter.isPending || confirmMatter.isPending
                ? 'Opening…'
                : 'Open the opposition'}
            </Button>
            {!opposition_enabled ? (
              <span
                className='text-micro text-block-fg'
                title='TMVERSE_OPPOSITION_ENABLED'
              >
                Opposition intake is switched off on this deployment. It is on by
                default, so someone turned it off deliberately — ask whoever runs
                this deployment to switch it back on.
              </span>
            ) : conflict && !conflict.clears ? (
              <span className='text-micro text-block-fg'>
                The conflict check has not cleared. Opening will be refused with
                the same answer.
              </span>
            ) : null}
          </div>
        </Card>
      </form>
    </div>
  )
}

/**
 * What the check found.
 *
 * Renders `clears` and `severity` as the server sent them. The acknowledge
 * button appears only on a non-exact hit, matching the server's own rule that
 * an exact name match is never waivable through the API — a button that
 * produced a refusal every time would read as a bug rather than as a rule.
 */
function ConflictResult({
  result,
  onAcknowledge,
}: {
  result: ConflictCheckResult
  onAcknowledge: (hit: ConflictHit) => void
}) {
  const tone = result.clears
    ? 'ok'
    : result.severity === 'CONFLICT'
      ? 'block'
      : 'warn'
  return (
    <div className='mt-3 rounded-[10px] border border-hairline p-3'>
      <div className='flex items-center gap-2'>
        <Badge tone={tone}>{result.severity}</Badge>
        {result.clears ? <Badge tone='outline'>clears</Badge> : null}
      </div>
      <p className='mt-2 max-w-[70ch] text-body'>{result.summary}</p>
      {result.hits.length ? (
        <ul className='mt-2 flex list-none flex-col gap-2 p-0'>
          {result.hits.map((hit) => {
            const acknowledged = result.waived.some(
              (name) => name.toLowerCase() === hit.matched_name.toLowerCase(),
            )
            return (
              <li
                key={`${hit.matched_name}:${hit.relationship}`}
                className='flex flex-wrap items-center justify-between gap-2 border-t border-hairline pt-2 first:border-t-0 first:pt-0'
              >
                <span className='text-body'>
                  <strong>{hit.matched_name}</strong>{' '}
                  <span className='text-muted'>— {hit.relationship}</span>
                </span>
                {hit.exact ? (
                  <Badge tone='block'>exact — not waivable</Badge>
                ) : acknowledged ? (
                  <Badge tone='ok'>acknowledged</Badge>
                ) : (
                  <Button
                    type='button'
                    size='sm'
                    variant='secondary'
                    onClick={() => onAcknowledge(hit)}
                  >
                    Different party — acknowledge
                  </Button>
                )}
              </li>
            )
          })}
        </ul>
      ) : null}
    </div>
  )
}

/** Which rule, if any, will compute this office's counterstatement deadline. */
function OfficeRuleNote({
  readiness,
}: {
  readiness: OfficeReadiness | undefined
}) {
  if (!readiness) return null
  if (!readiness.has_counterstatement_rule) {
    return (
      <p className='mt-3 max-w-[70ch] text-micro text-block-fg'>
        No counterstatement rule is registered for this office, so nothing here
        can check the date on the papers. Because missing a counterstatement
        ends the application, an unchecked date <strong>blocks</strong> the
        matter rather than warning about it.
      </p>
    )
  }
  // "the provisional India TMR opposition rule": the status and the office are
  // what a person weighs the date by, so they go in the sentence. The id of the
  // rule is for whoever is debugging a date, so it is the tooltip.
  const status = readiness.rule_status?.toLowerCase()
  const ruleName = `${status ? `${status} ` : ''}${JURISDICTION_OFFICES[readiness.office]} opposition rule`
  return (
    <p className='mt-3 max-w-[70ch] text-micro text-muted'>
      Computed by the{' '}
      <span
        title={readiness.rule_id ? `Rule ${readiness.rule_id}` : undefined}
        className='cursor-help underline decoration-dotted underline-offset-2'
      >
        {ruleName}
      </span>
      . {readiness.consequence}
    </p>
  )
}

/**
 * The series.
 *
 * `counted_from` is shown on every row, not just the first, because it is the
 * whole audit trail of a cascade: a downstream date that turns out to be wrong
 * is either the rule's fault or its anchor's, and only one of those two is
 * worth re-reading the papers for.
 */
function CascadeTable({
  steps,
  uncheckedReason,
  loading,
}: {
  steps: CascadeStep[]
  uncheckedReason: string | null
  loading: boolean
}) {
  if (loading) return <p className='mt-3 text-micro text-muted'>Computing…</p>
  if (!steps.length) {
    return uncheckedReason ? (
      <p className='mt-3 max-w-[70ch] text-body text-block-fg'>
        {uncheckedReason}
      </p>
    ) : null
  }
  return (
    <div className='mt-3 flex flex-col gap-2'>
      {steps.map((step) => (
        <div
          key={step.step}
          className='rounded-[10px] border border-hairline p-3'
        >
          <div className='flex flex-wrap items-center justify-between gap-2'>
            <div className='flex items-center gap-2'>
              <strong className='text-body'>{stepLabel(step.step)}</strong>
              <Badge tone={step.fatal ? 'block' : 'outline'}>
                {step.fatal ? 'fatal if missed' : 'watch'}
              </Badge>
              {step.rule_status === 'PROVISIONAL' ? (
                <Badge tone='warn'>provisional rule</Badge>
              ) : null}
            </div>
            <span className='font-mono text-body'>{step.due_date}</span>
          </div>
          <p className='mt-1 text-micro text-muted'>
            {step.counted_from_step
              ? `Counted from the ${stepLabel(step.counted_from_step).toLowerCase()} due date, ${step.counted_from}.`
              : `Counted from the notice, ${step.counted_from} — ${step.runs_from}.`}
          </p>
          <p className='mt-1 text-micro text-muted'>{step.consequence}</p>
          {step.open_questions.length ? (
            <details className='mt-2'>
              <summary
                className='cursor-pointer text-micro text-muted'
                title={`Rule ${step.rule_id}`}
              >
                {step.open_questions.length} open question
                {step.open_questions.length === 1 ? '' : 's'} on this rule
              </summary>
              <ul className='mt-1 flex flex-col gap-1 pl-4 text-micro text-muted'>
                {step.open_questions.map((question) => (
                  <li key={question}>{question}</li>
                ))}
              </ul>
            </details>
          ) : null}
        </div>
      ))}
    </div>
  )
}
